<template>
    <ui-modal title="Szczegóły worka" :closeable="true">
        <template #header>
            <h3 class="mb-1 text-center" v-text="'Szczegóły worka'" />
            <h5 class="mb-0 text-center" v-text="`ID ${bag.package_id}`" />
        </template>

        <h5 v-text="'Dane podstawowe'" />

        <div class="bag-basics bag-details-frame text-primary">
            <div class="bag-basics__col">
                <strong v-text="'Status'" />
                <span
                    class="status"
                    :class="{ 'is-success': isOpen, 'is-unavailable': !isOpen }"
                    v-text="isOpen ? 'Otwarty' : 'Zamknięty'"
                />
            </div>

            <div class="bag-basics__col">
                <strong v-text="'Frakcja'" />
                <fraction-label :fraction="fraction" />
            </div>

            <div class="bag-basics__col">
                <strong v-text="'Wartość'" />
                <div>
                    <strong v-text="total" /> ({{ quantity }} szt.)
                </div>
            </div>

            <div class="bag-basics__col">
                <strong v-text="'Nr plomby'" />
                <div>
                    <span v-html="$options.sealNumber(bag.seal_number, shouldHighlightSealPrefix)" />
                    <div v-if="!isOpen" v-text="operatorName" />
                </div>
            </div>
        </div>

        <h5 v-text="'Akcje na worku'" />

        <div class="bag-details-actions bag-details-frame text-primary">
            <div class="bag-details-actions__col">
                <h6 v-text="'Otwarcie'" class="mb-0" />

                <div>
                    <strong v-text="'Data:'" />
                    <template v-if="bag.opened_at">
                        {{ $moment(bag.opened_at).format('DD-MM-YYYY HH:mm') }}
                    </template>
                    <template v-else>
                        ---
                    </template>
                </div>

                <div>
                    <strong v-text="'ID urządzenia:'" />
                    {{ bag.mcuid || '---' }}
                </div>

                <div>
                    <strong v-text="'Otwarty przez:'" />
                    {{ bag.device_alias || '---' }}
                </div>

                <div>
                    <strong v-text="'Typ urządzenia:'" />
                    {{ openingDeviceType }}
                </div>
            </div>
            <div class="bag-details-actions__col">
                <h6 v-text="'Zamknięcie'" class="mb-0" />

                <div>
                    <strong v-text="'Data:'" />
                    <template v-if="bag.closed_at">
                        {{ $moment(bag.closed_at).format('DD-MM-YYYY HH:mm') }}
                    </template>
                    <template v-else>
                        ---
                    </template>
                </div>

                <div v-if="!wasClosedByCurrentUser">
                    <strong v-text="'ID urządzenia:'" />
                    {{ bag.mcuid_that_closed || '---' }}
                </div>

                <div>
                    <strong v-text="'Zamknięty przez:'" />
                    {{ closedBy }}
                </div>

                <div>
                    <strong v-text="'Operator:'" />
                    {{ reportOperatorName }}
                </div>
            </div>
        </div>

        <h5 v-text="'Statusy operatorskie'" />

        <div class="bag-details-actions bag-details-frame text-primary">
            <div
                v-for="{ id, label, status } in operatorStatuses"
                :key="id"
                class="bag-details-actions__col"
            >
                <strong>{{ label }}:</strong>

                <span class="d-flex align-items-center gap-0-5">
                    <a-icon
                        :icon="status.icon"
                        :class="status.class"
                        height="16"
                        width="16"
                    />
                    {{ status.message }}
                </span>

                <template v-if="status.error">
                    <span class="error-message">
                        Skopiuj treść błędu i przekaż go na HelpDesk
                        <a-icon
                            icon="Copy"
                            @click.native="copyError(status.error, id)"
                        />
                        <transition name="fade">
                            <span
                                v-if="copiedStatusId === id"
                                v-text="'Skopiowano'"
                                class="text-success"
                            />
                        </transition>
                    </span>

                    <the-text-area
                        readonly
                        :value="status.error"
                    />
                </template>
            </div>
        </div>

        <div class="bag-eans">
            <h5 v-text="`Zawartość worka (${quantity} szt.)`" />
            <the-btn
                text="Pokaż zawartość"
                class="btn-custom-gray btn-sm"
                :icon="showDetails ? 'ArrowUp' : 'ArrowDown'"
                @click="showDetails = !showDetails"
            />
        </div>

        <div v-if="showDetails" class="bag-details-frame">
            <bag-eans :bag="bag" />
        </div>
    </ui-modal>
</template>

<script>
import sealNumber from '@/helpers/seal-number'
import bagsMixin from '@/components/drs/stores/bags/bags.mixin'
import { DEVICE_TYPES } from '@/utils/constants/drs/store-devices'

export default {
    sealNumber,

    mixins: [bagsMixin],

    data: () => ({
        showDetails: false,
        copiedStatusId: null,
        copyTimeout: null
    }),

    computed: {
        bag() {
            return this.$store.getters.getModal.args.bag
        },

        openingDeviceType() {
            const deviceType = DEVICE_TYPES.find(({ value }) => value === this.bag.device_type)

            return deviceType?.label || this.bag.device_type || '---'
        },

        wasClosedByCurrentUser() {
            const currentUserEmail = this.$store.getters.getUser?.email

            return currentUserEmail && this.bag.cashier === currentUserEmail
        },

        closedBy() {
            const { cashier, device_alias_that_closed } = this.bag

            // Mobile app login is always an 8-digit number (e.g. "12345678")
            const isMobileAppLogin = /^\d{8}$/.test(cashier)

            // If cashier is missing or is a mobile app login, display device_alias_that_closed
            if (!cashier || isMobileAppLogin) {
                return device_alias_that_closed || '---'
            }

            return cashier
        },

        reportOperatorName() {
            return this.bag.report_package?.operator_name || '---'
        },

        operatorStatuses() {
            const { report_package, report_change_seal } = this.bag
            return [
                {
                    id: 'package',
                    label: 'Status zamknięcia worka',
                    status: this.operatorReportStatus(report_package)
                },
                {
                    id: 'seal',
                    label: 'Status zmiany plomby',
                    status: this.operatorReportStatus(report_change_seal)

                }
            ]
        }
    },

    beforeDestroy() {
        clearTimeout(this.copyTimeout)
    },

    methods: {
        async copyError(error, id) {
            try {
                await window.navigator.clipboard.writeText(error)
            } catch (e) {
                return
            }

            this.copiedStatusId = id
            clearTimeout(this.copyTimeout)
            this.copyTimeout = setTimeout(() => {
                this.copiedStatusId = null
            }, 2000)
        }
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.bag-details-frame {
    border: 1px solid var(--color-gray-200);
    border-radius: 0.5rem;
    padding: 1rem;
}

.bag-basics {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 2rem;
    width: 100%;

    &__col {
        flex: 1 1 200px;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
}

.bag-details-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;

    &__col {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .error-message .a-icon {
        color: var(--color-orange);
        cursor: pointer;
        margin-left: 0.25rem;
        vertical-align: sub;
        height: 1rem;
        width: 1rem;
    }
}

@media (max-width: 767px) {
    .bag-details-actions {
        grid-template-columns: 1fr;
    }
}

.bag-eans {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    background-color: var(--color-white);
    position: sticky;
    padding: 0.5rem 1rem 0.5rem 0;
    top: -18px;
    z-index: 1;
}
</style>